/**
 * @fileoverview Broadcast scheduling and validation utilities.
 * Provides helper functions for broadcast message timing, node validation,
 * and date picker configuration.
 */

import store from "@/store";
import moment from "moment";
import { IBroadcast } from "@/store/modules/broadcast/types";

/**
 * Checks if a timestamp is in the past.
 *
 * @param {number | string} timestamp - Unix timestamp in milliseconds
 * @returns {boolean} True if the timestamp is before the current time
 *
 * @example
 * if (isPast(broadcast.timestamp)) {
 *   // Broadcast has already been sent
 * }
 */
export function isPast(timestamp: number | string): boolean {
  // FIXME: remove me once this new implementation is correct
  // const selectedBroadcastId = store.state.broadcast.selectedBroadcastId;
  // if (timestamp && selectedBroadcastId) {
  //   return moment.unix(Number(selectedBroadcastId) / 1000).isBefore(moment());
  // } else {
  //   return false;
  // }

  if (timestamp) {
    return moment.unix(Number(timestamp) / 1000).isBefore(moment());
  } else {
    return false;
  }
}

/**
 * Validates that a node exists in the store.
 *
 * @param {string} nodeId - The node identifier to validate
 * @returns {boolean} True if the node exists in the content store
 *
 * @example
 * if (isValidNode(broadcast.nodeId)) {
 *   // Node exists and can be used
 * }
 */
export function isValidNode(nodeId: string): boolean {
  const node = store.state.nodes.content[nodeId];
  const isValidNode = !!node;
  return isValidNode;
}

/**
 * Returns CSS class name for highlighting broadcast rows based on their timing.
 *
 * @param {Object} params - Function parameters
 * @param {IBroadcast} params.row - The broadcast row data
 * @returns {string} CSS class name ('warning-broadcast-row' for future, 'past-broadcast-row' for past)
 *
 * @example
 * const className = highlightPendingBroadcasts({ row: broadcast });
 * // Returns: 'warning-broadcast-row' or 'past-broadcast-row'
 */
export function highlightPendingBroadcasts({ row }: { row: IBroadcast }): string {
  let className = "past-broadcast-row";
  const isFuture = !isPast(row.timestamp);

  if (row && isFuture) {
    className = "warning-broadcast-row";
  }

  return className;
}

/**
 * Date picker configuration options for broadcast scheduling.
 * Disables past dates and provides shortcuts for common scheduling times.
 *
 * @constant
 * @type {Object}
 * @property {Function} disabledDate - Function that disables dates before yesterday
 * @property {Array} shortcuts - Quick select options for Today, Tomorrow, and A week later
 *
 * @example
 * <el-date-picker :picker-options="pickerOptions" />
 */
export const pickerOptions = {
  disabledDate(time: Date) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return time.getTime() < yesterday.getTime();
  },
  shortcuts: [
    {
      text: "Today",
      onClick(picker: any) {
        picker.$emit("pick", new Date());
      },
    },
    {
      text: "Tomorrow",
      onClick(picker: any) {
        const date = new Date();
        date.setTime(date.getTime() + 3600 * 1000 * 24);
        picker.$emit("pick", date);
      },
    },
    {
      text: "A week later",
      onClick(picker: any) {
        const date = new Date();
        date.setTime(date.getTime() + 3600 * 1000 * 24 * 7);
        picker.$emit("pick", date);
      },
    },
  ],
};
